import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, Loader2, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { API, formatAUD } from "../lib/shop";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export default function CheckoutPage() {
  const { items, total } = useCart();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("new");
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    line1: "", line2: "", suburb: "", state: "NSW", postcode: "",
  });

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || user.name || "",
      customer_email: f.customer_email || user.email || "",
    }));
    axios.get(`${API}/addresses`).then((r) => setSavedAddresses(r.data)).catch(() => toast.error("Could not load saved addresses"));
  }, [user]);

  if (items.length === 0 && !submitting) return <Navigate to="/cart" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickAddress = (addr) => {
    setSelectedAddressId(addr ? addr.id : "new");
    setForm((f) => ({
      ...f,
      line1: addr ? addr.line1 : "",
      line2: addr ? addr.line2 : "",
      suburb: addr ? addr.suburb : "",
      state: addr ? addr.state : "NSW",
      postcode: addr ? addr.postcode : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data: order } = await axios.post(`${API}/orders`, {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        address: {
          line1: form.line1, line2: form.line2, suburb: form.suburb,
          state: form.state, postcode: form.postcode, country: "Australia",
        },
        items: items.map((i) => ({ variant_id: i.variant_id, quantity: i.qty })),
      });
      // Create Stripe Checkout session and redirect — cart is cleared after payment succeeds
      const { data: payment } = await axios.post(`${API}/payments/checkout`, {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      window.location.href = payment.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8" data-testid="checkout-page">
      <Link to="/cart" data-testid="back-to-cart-link" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary">
        <ChevronLeft className="h-4 w-4" /> Back to cart
      </Link>
      <h1 className="mt-4 text-3xl font-bold tracking-tight">Checkout</h1>

      <form onSubmit={handleSubmit} className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="space-y-8">
          <section className="rounded-sm border border-border bg-white p-6">
            <h2 className="text-base font-semibold">Your Details</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name *</Label>
                <Input id="name" required value={form.customer_name} onChange={set("customer_name")} data-testid="checkout-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" required value={form.customer_email} onChange={set("customer_email")} data-testid="checkout-email-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={form.customer_phone} onChange={set("customer_phone")} data-testid="checkout-phone-input" />
              </div>
            </div>
          </section>

          <section className="rounded-sm border border-border bg-white p-6">
            <h2 className="text-base font-semibold">Delivery Address</h2>
            {!user && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="checkout-signin-hint">
                <Link to="/login" className="font-medium text-secondary hover:underline">Sign in</Link> to reuse saved delivery addresses.
              </p>
            )}
            {user && savedAddresses.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="saved-addresses-list">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <BookMarked className="h-3.5 w-3.5" /> Saved addresses
                </p>
                {savedAddresses.map((a) => (
                  <label
                    key={a.id}
                    data-testid={`saved-address-${a.id}`}
                    className={`flex cursor-pointer items-center gap-3 rounded-sm border p-3 text-sm transition-colors ${
                      selectedAddressId === a.id ? "border-secondary bg-secondary/5" : "border-border hover:border-secondary/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="saved-address"
                      checked={selectedAddressId === a.id}
                      onChange={() => pickAddress(a)}
                      className="accent-[#192d55]"
                    />
                    <span>
                      {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.suburb} {a.state} {a.postcode}
                    </span>
                  </label>
                ))}
                <label
                  data-testid="saved-address-new"
                  className={`flex cursor-pointer items-center gap-3 rounded-sm border p-3 text-sm transition-colors ${
                    selectedAddressId === "new" ? "border-secondary bg-secondary/5" : "border-border hover:border-secondary/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="saved-address"
                    checked={selectedAddressId === "new"}
                    onChange={() => pickAddress(null)}
                    className="accent-[#192d55]"
                  />
                  <span>Use a new address</span>
                </label>
              </div>
            )}
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="line1">Address line 1 *</Label>
                <Input id="line1" required value={form.line1} onChange={set("line1")} data-testid="checkout-address1-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="line2">Address line 2</Label>
                <Input id="line2" value={form.line2} onChange={set("line2")} data-testid="checkout-address2-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="suburb">Suburb *</Label>
                <Input id="suburb" required value={form.suburb} onChange={set("suburb")} data-testid="checkout-suburb-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>State *</Label>
                  <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                    <SelectTrigger data-testid="checkout-state-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATES.map((s) => <SelectItem key={s} value={s} data-testid={`state-option-${s}`}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="postcode">Postcode *</Label>
                  <Input id="postcode" required pattern="[0-9]{4}" title="4-digit postcode" value={form.postcode} onChange={set("postcode")} data-testid="checkout-postcode-input" />
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="h-fit rounded-sm border border-border bg-white p-6" data-testid="checkout-summary">
          <h2 className="text-base font-semibold">Order Summary</h2>
          <ul className="mt-4 space-y-3 border-t border-border pt-4">
            {items.map((i) => (
              <li key={i.variant_id} className="flex justify-between gap-2 text-sm">
                <span className="text-muted-foreground">
                  <span className="font-mono text-xs">{i.product_code}</span> × {i.qty}
                </span>
                <span className="tabular-nums">{formatAUD(i.qty * i.unit_price)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-between border-t border-border pt-4 text-base font-bold">
            <span>Total (AUD)</span>
            <span className="tabular-nums text-primary" data-testid="checkout-total">{formatAUD(total)}</span>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="place-order-button"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-sm bg-accent px-4 py-3 text-sm font-bold text-accent-foreground transition-colors hover:bg-[#dfa557] disabled:opacity-60"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Redirecting to payment..." : "Pay with Card"}
          </button>
          <p className="mt-3 text-xs text-muted-foreground">
            Secure card payment powered by Stripe. You'll be redirected to complete payment.
          </p>
        </div>
      </form>
    </div>
  );
}
